import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { ConfirmProvider } from "./hooks/useConfirm";
import { TimezoneProvider } from "./context/TimezoneContext";
import { ScrollRestoration } from "./components/ScrollRestoration";
import FloatingDock from "./components/FloatingDock";
import RefreshControl from "./components/RefreshControl";
import GuidedWalkthrough from "./components/walkthrough/GuidedWalkthrough";
import { TabsProvider } from "./context/TabsContext";
import { CustomerRoute } from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import UpdatePrompt from "./components/UpdatePrompt";
import ScheduledCallBubble from './components/chat/ScheduledCallBubble';
import SystemAlertGate from "./components/SystemAlertGate";
import StaffTabKeepAliveShell from "./components/StaffTabKeepAliveShell";

// Eager imports (tiny / used in the OAuth early-return or as shared wrappers)
import AuthCallback from "./pages/AuthCallback";
import { CustomerLayout } from "./components/DashboardLayout";
import { ChatProvider } from "./context/ChatContext";
import { ThemeProvider } from "./context/ThemeContext";
import NativeNavigationBridge from "./hooks/useNativeNavigation";

// ---------------------------------------------------------------------------
// Lazy-loaded route pages (code-splitting). Every page becomes its own chunk
// so the initial/main JS bundle stays small. This makes the app re-paint fast
// whenever the Android WebView reloads the site (e.g. when it returns from the
// system file chooser and the OS had reclaimed the app), instead of forcing a
// slow ~6MB re-download that shows a long black screen.
// ---------------------------------------------------------------------------

// Public / auth pages
const Landing = lazy(() => import("./pages/Landing"));
const OperationManual = lazy(() => import("./pages/OperationManual"));
const Download = lazy(() => import("./pages/Download"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const SocialRegister = lazy(() => import("./pages/SocialRegister"));
const StaffRegister = lazy(() => import("./pages/StaffRegister"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const RegistrationSuccess = lazy(() => import("./pages/auth/Success"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));

// Public pages
const GuarantorVerification = lazy(() => import("./pages/public/GuarantorVerification"));
const GuarantorDashboard = lazy(() => import("./pages/public/GuarantorDashboard"));
const RecruitmentApply = lazy(() => import("./pages/public/RecruitmentApply"));
const RecruitmentOffer = lazy(() => import("./pages/public/RecruitmentOffer"));
const RecruitmentInterviewConfirm = lazy(() => import("./pages/public/RecruitmentInterviewConfirm"));
const RecruitmentGuarantor = lazy(() => import("./pages/public/RecruitmentGuarantor"));
const StaffGuarantor = lazy(() => import("./pages/public/StaffGuarantor"));
const WitnessSign = lazy(() => import("./pages/public/WitnessSign"));

// Customer pages
const CustomerDashboard = lazy(() => import("./pages/customer/Dashboard"));
const LoanApplication = lazy(() => import("./pages/customer/LoanApplication"));
const CustomerLoans = lazy(() => import("./pages/customer/Loans"));
const LoanDetails = lazy(() => import("./pages/customer/LoanDetails"));
const CustomerPayments = lazy(() => import("./pages/customer/Payments"));
const CustomerProfile = lazy(() => import("./pages/customer/Profile"));
const CustomerSettings = lazy(() => import("./pages/customer/Settings"));
const PaymentSuccess = lazy(() => import("./pages/customer/PaymentSuccess"));
const ApplicationDetail = lazy(() => import("./pages/customer/ApplicationDetail"));
const TopUpCalculator = lazy(() => import("./pages/customer/TopUpCalculator"));
const MigrationTopUp = lazy(() => import("./pages/customer/MigrationTopUp"));
const CustomerRefunds = lazy(() => import("./pages/customer/MyRefunds"));
const MyTransactions = lazy(() => import("./pages/customer/MyTransactions"));
const CustomerFees = lazy(() => import("./pages/customer/Fees"));
const CustomerChat = lazy(() => import("./pages/customer/Chat"));
const CustomerSignature = lazy(() => import("./pages/customer/CustomerSignature"));
const BankChangeRequest = lazy(() => import("./pages/customer/BankChangeRequest"));
const UpdateInfoRequest = lazy(() => import("./pages/customer/UpdateInfoRequest"));

// Investor pages
const InvestorRegister = lazy(() => import("./pages/investor/Register"));
const InvestorRegisterPublic = lazy(() => import("./pages/investor/InvestorRegister"));
const InvestorReferralRegister = lazy(() => import("./pages/investor/InvestorReferralRegister"));
const InvestorDashboard = lazy(() => import("./pages/investor/Dashboard"));
const InvestorProfile = lazy(() => import("./pages/investor/Profile"));
const InvestmentProducts = lazy(() => import("./pages/investor/Products"));
const MyInvestments = lazy(() => import("./pages/investor/Investments"));
const InvestorWithdrawals = lazy(() => import("./pages/investor/Withdrawals"));
const WithdrawalRequestForm = lazy(() => import("./pages/investor/WithdrawalRequestForm"));
const InvestorStatement = lazy(() => import("./pages/investor/Statement"));
const ReferInvestor = lazy(() => import("./pages/investor/ReferInvestor"));

// White (never black) loading fallback shown while a route chunk downloads.
const RouteFallback = () => (
  <div
    className="min-h-screen w-full flex items-center justify-center bg-white"
    data-testid="route-loading"
  >
    <div className="w-8 h-8 rounded-full border-2 border-forest-600 border-t-transparent animate-spin" />
  </div>
);

function AppRouter() {
  const location = useLocation();

  // Check for OAuth callback - session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Every /staff/* and /admin/* page (except the pre-auth login/register
  // screens) goes through the persistent tab-keep-alive shell instead of
  // the normal <Routes> below — see StaffTabKeepAliveShell for why.
  const isStaffArea = (location.pathname.startsWith('/staff') || location.pathname.startsWith('/admin'))
    && location.pathname !== '/staff/login' && location.pathname !== '/staff/register';

  if (isStaffArea) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <StaffTabKeepAliveShell />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/download" element={<Download />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/social/register" element={<SocialRegister />} />
      <Route path="/install/navigation" element={<Navigate to="/download" replace />} />
      <Route path="/install/app" element={<Navigate to="/download" replace />} />
      <Route path="/staff/register" element={<StaffRegister />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/success" element={<RegistrationSuccess />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />

      {/* Public Guarantor Verification (no auth) */}
      <Route path="/guarantor/verify/:verificationId" element={<GuarantorVerification />} />
      <Route path="/guarantor/dashboard" element={<GuarantorDashboard />} />

      {/* Public Recruitment (no auth) */}
      <Route path="/recruitment/apply/:openingId" element={<RecruitmentApply />} />
      <Route path="/recruitment/offer/:token" element={<RecruitmentOffer />} />
      <Route path="/recruitment/interview/:token" element={<RecruitmentInterviewConfirm />} />
      <Route path="/recruitment/guarantor/:token" element={<RecruitmentGuarantor />} />
      <Route path="/recruitment/staff-guarantor/:token" element={<StaffGuarantor />} />
      <Route path="/recruitment/witness/:token" element={<WitnessSign />} />

      {/* Customer Routes */}
      <Route path="/customer/dashboard" element={
        <CustomerRoute><CustomerDashboard /></CustomerRoute>
      } />
      <Route path="/customer/apply" element={
        <CustomerRoute><LoanApplication /></CustomerRoute>
      } />
      <Route path="/customer/loans" element={
        <CustomerRoute><CustomerLoans /></CustomerRoute>
      } />
      <Route path="/customer/loans/:loanId" element={
        <CustomerRoute><LoanDetails /></CustomerRoute>
      } />
      <Route path="/customer/payments" element={
        <CustomerRoute><CustomerPayments /></CustomerRoute>
      } />
      <Route path="/customer/profile" element={
        <CustomerRoute><CustomerProfile /></CustomerRoute>
      } />
      <Route path="/customer/payment-success" element={
        <CustomerRoute><PaymentSuccess /></CustomerRoute>
      } />
      <Route path="/customer/applications/:applicationId" element={
        <CustomerRoute><ApplicationDetail /></CustomerRoute>
      } />
      <Route path="/customer/topup-calculator" element={
        <CustomerRoute><TopUpCalculator /></CustomerRoute>
      } />
      <Route path="/customer/manual" element={
        <CustomerRoute><OperationManual /></CustomerRoute>
      } />
      <Route path="/customer/migration-topup" element={
        <CustomerRoute><MigrationTopUp /></CustomerRoute>
      } />
      <Route path="/customer/refunds" element={
        <CustomerRoute><CustomerRefunds /></CustomerRoute>
      } />
      <Route path="/customer/transactions" element={
        <CustomerRoute><MyTransactions /></CustomerRoute>
      } />
      <Route path="/customer/fees" element={
        <CustomerRoute><CustomerFees /></CustomerRoute>
      } />
      <Route path="/customer/settings" element={
        <CustomerRoute><CustomerSettings /></CustomerRoute>
      } />
      <Route path="/customer/chat" element={
        <CustomerRoute><CustomerChat /></CustomerRoute>
      } />
      <Route path="/customer/signature" element={
        <CustomerRoute><CustomerSignature /></CustomerRoute>
      } />
      <Route path="/customer/bank-change" element={
        <CustomerRoute><BankChangeRequest /></CustomerRoute>
      } />
      <Route path="/customer/update-info" element={
        <CustomerRoute><UpdateInfoRequest /></CustomerRoute>
      } />

      {/* Investor Public Routes */}
      <Route path="/investor-register/refer" element={<InvestorReferralRegister />} />
      <Route path="/investor-register/:token" element={<InvestorRegisterPublic />} />

      {/* Investor Routes */}
      <Route path="/investor/register" element={
        <CustomerRoute><CustomerLayout><InvestorRegister /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/dashboard" element={
        <CustomerRoute><CustomerLayout><InvestorDashboard /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/products" element={
        <CustomerRoute><CustomerLayout><InvestmentProducts /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/investments" element={
        <CustomerRoute><CustomerLayout><MyInvestments /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/withdrawals" element={
        <CustomerRoute><CustomerLayout><InvestorWithdrawals /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/withdrawals/new" element={
        <CustomerRoute><CustomerLayout><WithdrawalRequestForm /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/statement" element={
        <CustomerRoute><CustomerLayout><InvestorStatement /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/refer" element={
        <CustomerRoute><CustomerLayout><ReferInvestor /></CustomerLayout></CustomerRoute>
      } />
      <Route path="/investor/profile" element={
        <CustomerRoute><CustomerLayout><InvestorProfile /></CustomerLayout></CustomerRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// Android back button: on a root page (dashboard/home) the app must MINIMISE, not walk history back to the login page.
const ROOT_PATHS = ['/', '/staff', '/staff/dashboard', '/staff/login', '/login', '/customer/dashboard', '/customer', '/investor/dashboard', '/investor', '/dashboard'];
window.__bbcHandleBack = () => {
  const p = window.location.pathname.replace(/\/+$/, '') || '/';
  if (document.querySelector('[role="dialog"][data-state="open"], [data-testid="video-call"], [data-testid="incoming-call-dialog"]')) return 'noop';
  return ROOT_PATHS.includes(p) ? 'root' : 'noop';
};

function App() {
  // Initialize layout mode from localStorage on app load
  useEffect(() => {
    const layoutMode = localStorage.getItem('bbc_layout_mode') || 'modern';
    if (layoutMode === 'classic') {
      document.documentElement.classList.add('classic-layout');
      document.documentElement.classList.remove('modern-layout');
    } else {
      document.documentElement.classList.add('modern-layout');
      document.documentElement.classList.remove('classic-layout');
    }
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <TimezoneProvider>
            <ChatProvider>
            <ConfirmProvider>
            <TabsProvider>
              <AppRouter />
              <ScrollRestoration />
              <FloatingDock />
              <RefreshControl />
              <GuidedWalkthrough />
              <Toaster 
                position="top-center" 
                richColors 
                expand={true}
                toastOptions={{
                  className: 'max-w-[90vw] sm:max-w-md',
                  style: {
                    maxWidth: '90vw',
                  },
                }}
              />
              <PWAInstallPrompt />
              <UpdatePrompt />
              <ScheduledCallBubble />
              <SystemAlertGate />
              <NativeNavigationBridge />
            </TabsProvider>
            </ConfirmProvider>
            </ChatProvider>
            </TimezoneProvider>
          </AuthProvider>
        </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
